import React, { useRef, useEffect, useMemo } from 'react';
import { createChart, CandlestickSeries, createSeriesMarkers } from 'lightweight-charts';

function SummaryCards({ summary }) {
    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {[
                { title: 'Total Trades', value: summary.total_trades || 0, icon: '📊',  },
                { title: 'Win Rate', value: `${(summary.win_rate || 0).toFixed(2)}%`, icon: '📈', },
                { title: 'Total PnL', value: `$${(summary.total_pnl || 0).toFixed(2)}`, icon: '💰', },
                { title: 'ROI', value: `${(summary.roi || 0).toFixed(2)}%`, icon: '↗️' },
            ].map((card, idx) => (
                <div
                    key={idx}
                    className="p-3 flex justify-between items-center rounded-2xl shadow-xl text-center transform hover:scale-105 transition-all duration-300"
                    style={{
                        border: '1px solid rgba(255,255,255,0.2)',
                        color: 'var(--color-foreground)'
                    }}
                >
                    <div className="flex items-center justify-center gap-2">
                        <span className="text-xl" style={{ color: 'rgba(255,255,255,0.8)' }}>{card.icon}</span>
                        <p className="text-xs uppercase tracking-wide text-gray-300 ">{card.title}</p>

                    </div>
                    <p className="text-xs  text-gray-200" >{card.value}</p>
                </div>
            ))}
        </div>
    );
}

function PriceChart({ results, processedTrades }) {
    const containerRef = useRef(null);
    const chartRef = useRef(null);
    const seriesRef = useRef(null);
    const markersInstanceRef = useRef(null);
    const customMarkersRef = useRef([]);

    useEffect(() => {
        if (!containerRef.current) return;

        const chart = createChart(containerRef.current, {
            width: containerRef.current.clientWidth,
            height: 400,
            layout: {
                background: { color: "#0f0f23" },
                textColor: "#ffffff",
            },
            grid: {
                vertLines: { color: "#2a2a4a" },
                horzLines: { color: "#2a2a4a" },
            },
            rightPriceScale: {
                borderColor: "#4a4a6a",
                textColor: "#fff",
                scaleMargins: { top: 0.2, bottom: 0.1 },
            },
            timeScale: {
                timeVisible: true,
                borderColor: "#4a4a6a",
            },
            crosshair: {
                mode: 0,
                horzLine: { color: "#888", width: 1 },
                vertLine: { color: "#888", width: 1 },
            },
        });

        chartRef.current = chart;

        const series = chart.addSeries(CandlestickSeries, {
            upColor: "#10b981",
            downColor: "#ef4444",
            borderVisible: false,
            wickUpColor: "#10b981",
            wickDownColor: "#ef4444",
            borderUpColor: "#10b981",
            borderDownColor: "#ef4444",
        });
        seriesRef.current = series;

        const tooltip = document.createElement("div");
        tooltip.style.position = "absolute";
        tooltip.style.background = "rgba(15,15,23,0.95)";
        tooltip.style.color = "var(--color-foreground)";
        tooltip.style.padding = "12px";
        tooltip.style.borderRadius = "12px";
        tooltip.style.pointerEvents = "none";
        tooltip.style.zIndex = "1000";
        tooltip.style.border = `1px solid var(--color-border)`;
        tooltip.style.fontSize = "14px";
        tooltip.style.backdropFilter = "blur(20px)";
        tooltip.style.boxShadow = "0 10px 25px rgba(0,0,0,0.5)";
        tooltip.style.display = "none";
        containerRef.current.appendChild(tooltip);

        chart.subscribeCrosshairMove((param) => {
            if (!param.time || !param.seriesData) {
                tooltip.style.display = "none";
                return;
            }

            const data = param.seriesData.get(seriesRef.current);
            if (!data) {
                tooltip.style.display = "none";
                return;
            }

            const markers = customMarkersRef.current;
            const marker = markers.find((m) => m.time === data.time);

            if (marker) {
                tooltip.innerHTML = `
                    <div style="font-weight: bold; margin-bottom: 4px;">Trade Details</div>
                    <div><strong>Price:</strong> $${(marker.price || 0).toFixed(2)}</div>
                    <div><strong>Side:</strong> ${marker.side || 'N/A'}</div>
                    <div><strong>Action:</strong> ${marker.action || 'N/A'}</div>
                    <div><strong>Cycle:</strong> ${marker.cycle || 0}</div>
                `;
                tooltip.style.display = "block";
                // Ensure tooltip stays within bounds
                const rect = containerRef.current.getBoundingClientRect();
                let left = param.point.x + 10;
                let top = param.point.y - 10;
                if (left + tooltip.offsetWidth > rect.right) {
                    left = param.point.x - tooltip.offsetWidth - 10;
                }
                if (top < rect.top) {
                    top = param.point.y + 10;
                }
                tooltip.style.left = `${left}px`;
                tooltip.style.top = `${top}px`;
            } else {
                tooltip.style.display = "none";
            }
        });

        const handleResize = () => {
            if (chartRef.current && containerRef.current) {
                chartRef.current.applyOptions({ width: containerRef.current.clientWidth });
            }
        };
        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
            if (chartRef.current) chartRef.current.remove();
            if (tooltip.parentNode) tooltip.parentNode.removeChild(tooltip);
        };
    }, []);

    useEffect(() => {
        if (!results?.ohlc_data || !seriesRef.current) return;

        const ohlc = results.ohlc_data.map((d) => ({
            time: Math.floor(d.timestamp / 1000),
            open: parseFloat(d.open.toFixed(2)),
            high: parseFloat(d.high.toFixed(2)),
            low: parseFloat(d.low.toFixed(2)),
            close: parseFloat(d.close.toFixed(2)),
        }));

        seriesRef.current.setData(ohlc);

        const markers = processedTrades
            .map((trade) => {
                const tradeTimestamp = Math.floor(new Date(trade.date).getTime() / 1000);
                let candle = ohlc.reduce((closest, current) => {
                    const currentDiff = Math.abs(current.time - tradeTimestamp);
                    const closestDiff = Math.abs(closest.time - tradeTimestamp);
                    return currentDiff < closestDiff ? current : closest;
                }, ohlc[0]);

                if (!candle || Math.abs(candle.time - tradeTimestamp) > 86400) {
                    candle = ohlc.find(
                        (c) => trade.price >= Math.min(c.open, c.close) && trade.price <= Math.max(c.open, c.close)
                    );
                }

                if (!candle) return null;

                let color, shape, position;

                const action = trade.action.toUpperCase();
                if (action.includes('BUY') || action.includes('ENTRY') || action.includes('OPEN')) {
                    color = "#FFD700";
                    shape = "arrowUp";
                    position = "belowBar";
                } else if (action.includes('SELL') || action.includes('TP') || action.includes('TAKE_PROFIT')) {
                    color = "#00FF00";
                    shape = "circle";
                    position = "aboveBar";
                } else if (action.includes('STOP') || action.includes('LOSS') || action.includes('LIQUIDATION')) {
                    color = "#FF4C4C";
                    shape = "arrowDown";
                    position = "aboveBar";
                } else {
                    // Default for any other action
                    color = "#888888";
                    shape = "square";
                    position = "aboveBar";
                }

                return {
                    time: candle.time,
                    position,
                    shape,
                    color,
                    price: trade.price,
                    side: trade.side,
                    action: trade.action,
                    cycle: trade.cycle,
                    text: `${trade.side} ${trade.action} (Cycle ${trade.cycle})`,
                };
            })
            .filter(Boolean);

        if (!markersInstanceRef.current) {
            markersInstanceRef.current = createSeriesMarkers(seriesRef.current, markers);
        } else {
            markersInstanceRef.current.setMarkers(markers);
        }
        customMarkersRef.current = markers;
    }, [results, processedTrades]);

    return (
        <div className=" from-indigo-900 via-purple-900 to-pink-900 p-6 rounded-2xl shadow-2xl border border-indigo-500/30 mb-8">
            <div className="flex items-center mb-4">
                <span className="text-2xl mr-2">📈</span>
                <h2 className="text-2xl font-bold text-white">Price Chart with Trades</h2>
            </div>
            <div className="bg-gray-900/50 rounded-xl p-4 border border-gray-700/50">
                <div ref={containerRef} className="w-full h-96 rounded-lg" />
            </div>
        </div>
    );
}

function TradeHistoryTable({ processedTrades }) {
    if (!processedTrades || processedTrades.length === 0) {
        return (
            <div
                className="p-6 rounded-2xl shadow-xl mb-8"
                style={{
                    background: 'linear-gradient(to right, var(--color-secondary), var(--color-muted))',
                    border: '1px solid var(--color-border)',
                    color: 'var(--color-foreground)'
                }}
            >
                <div className="flex items-center mb-4">
                    <h2 className="text-2xl font-bold" style={{ color: 'var(--color-foreground)' }}>Trade History</h2>
                </div>
                <div className="text-center py-8">
                    <p className="text-lg font-semibold" style={{ color: 'var(--color-muted-foreground)' }}>No Data Available</p>
                </div>
            </div>
        );
    }

    return (
        <div
            className="p-6 rounded-2xl shadow-xl mb-8"
            style={{
                background: 'linear-gradient(to right, var(--color-secondary), var(--color-muted))',
                border: '1px solid var(--color-border)',
                color: 'var(--color-foreground)'
            }}
        >
            <div className="flex items-center mb-4">
               
                <h2 className="text-2xl font-bold" style={{ color: 'var(--color-foreground)' }}>Trade History</h2>
            </div>
            <div className="relative h-[420px] overflow-y-auto overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-700">
                    <thead className="sticky top-0 z-20 bg-black">
                        <tr>
                            <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider sm:px-6" style={{ color: 'var(--color-muted-foreground)' }}>#</th>
                            <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider sm:px-6" style={{ color: 'var(--color-muted-foreground)' }}>Date</th>
                           
                            <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider sm:px-6" style={{ color: 'var(--color-muted-foreground)' }}>Action</th>
                            <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider sm:px-6" style={{ color: 'var(--color-muted-foreground)' }}>Price</th>
                            <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider sm:px-6" style={{ color: 'var(--color-muted-foreground)' }}>Quantity</th>
                            <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider sm:px-6" style={{ color: 'var(--color-muted-foreground)' }}>PnL ($)</th>
                            <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider sm:px-6" style={{ color: 'var(--color-muted-foreground)' }}>Fee ($)</th>
                            <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider sm:px-1" style={{ color: 'var(--color-muted-foreground)' }}>Cumulative Profit</th>

                        </tr>
                    </thead>
                    <tbody style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
                        {processedTrades.map((trade, index) => {
                            const action = trade.action.toUpperCase();
                            let badgeColor = 'rgba(128,128,128,0.2)';
                            let badgeTextColor = '#888';
                            if (action.includes('BUY') || action.includes('ENTRY') || action.includes('OPEN')) {
                                badgeColor = 'rgba(255,215,0,0.2)';
                                badgeTextColor = '#FFD700';
                            } else if (action.includes('SELL') || action.includes('TP') || action.includes('TAKE_PROFIT')) {
                                badgeColor = 'rgba(0,255,0,0.2)';
                                badgeTextColor = '#00FF00';
                            } else if (action.includes('STOP') || action.includes('LOSS') || action.includes('LIQUIDATION')) {
                                badgeColor = 'rgba(255,76,76,0.2)';
                                badgeTextColor = '#FF4C4C';
                            }

                            const pnlValue = trade.unrealized_pnl !== null ? trade.unrealized_pnl : (trade.pnl || 0);
                            const feeValue = (trade.fee || 0);
                            const quantityValue = (trade.quantity || 0);
                            const priceValue = (trade.price || 0);

                            return (
                                <tr key={index} className="hover:bg-gray-700/30 transition-colors duration-200">
                                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium sm:px-6" style={{ color: 'var(--color-muted-foreground)' }}>{index + 1}</td>
                                    <td className="px-4 py-4 whitespace-nowrap text-sm sm:px-6" style={{ color: 'var(--color-foreground)' }}>{trade.date}</td>
                                    
                                    <td className="px-4 py-4 whitespace-nowrap text-sm sm:px-6" style={{ color: 'var(--color-foreground)' }}>{trade.action || 'N/A'}</td>
                                    <td className="px-4 py-4 whitespace-nowrap text-sm text-right sm:px-6" style={{ color: 'var(--color-foreground)' }}>${priceValue.toFixed(2)}</td>
                                    <td className="px-4 py-4 whitespace-nowrap text-sm text-right sm:px-6" style={{ color: 'var(--color-foreground)' }}>{quantityValue.toFixed(6)}</td>
                                    <td className={`px-4 py-4 whitespace-nowrap text-sm text-right font-bold sm:px-6 ${pnlValue > 0 ? 'text-green-400' : pnlValue < 0 ? 'text-red-400' : 'text-gray-300'}`}>
                                        ${pnlValue.toFixed(2)}
                                    </td>
                                    <td className="px-4 py-4 whitespace-nowrap text-sm text-right sm:px-6" style={{ color: 'var(--color-muted-foreground)' }}>${feeValue.toFixed(2)}</td>
                                    <td className="px-4 py-4 whitespace-nowrap text-sm text-right sm:px-6" style={{ color: 'var(--color-muted-foreground)' }}>${trade.cumulative_pnl.toFixed(2)}</td>

                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function CurrentPosition({ currentPos }) {
    return (
        <div
            className="p-6 rounded-2xl shadow-2xl mb-8"
            style={{
                // background: 'linear-gradient(to bottom right, var(--color-secondary), #FFD700)', 
                border: '1px solid var(--color-border)',
                color: 'var(--color-foreground)'
            }}
        >
            <div className="flex items-center mb-4">
               
                <h2 className="text-2xl font-bold" style={{ color: 'var(--color-foreground)' }}>Current Position</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                    { label: 'Entry Price', value: `$${(currentPos.entry_price || 0).toFixed(2)}`, icon: '💰', color: '#FFD700' },
                    { label: 'Unrealized PnL', value: `$${(currentPos.unrealized_pnl || 0).toFixed(2)}`, icon: '📈', color: '#00FF00' },
                    { label: 'Liquidation Price', value: `$${(currentPos.liquidation_price || 0).toFixed(2)}`, icon: '⚠️', color: '#FF4C4C' },
                    { label: 'Take Profit', value: `$${(currentPos.take_profit_price || 0).toFixed(2)}`, icon: '🎯', color: '#00FF00' },
                    { label: 'Quantity', value: (currentPos.quantity || 0).toFixed(6), icon: '📊', color: '#FFD700' },
                    { label: 'Investment', value: `$${(currentPos.current_investment || 0)}`, icon: '💎', color: '#8b5cf6' },
                ].map((item, idx) => (
                    <div
                        key={idx}
                        className="p-2 flex justify-between   rounded-xl text-center"
                        style={{
                          
                            border: '1px solid rgba(255,255,255,0.2)',
                            color: 'var(--color-foreground)'
                        }}
                    >
                        <div className="flex items-center justify-center gap-2 ">
                            <span className="text-lg">{item.icon}</span>
                            <p className="text-xs uppercase tracking-wide " style={{ color: 'var(--color-muted-foreground)' }}>{item.label}</p>

                        </div>
                        <p className={`text-lg font-bold`} >{item.value}</p>
                    </div>
                ))}
            </div>
        </div>
    );
}

import {
    BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
    ResponsiveContainer, ComposedChart, Legend, ReferenceLine,Cell,PieChart,
    Pie,LabelList
  } from 'recharts';
  
  // ==================== INVESTMENT PROGRESSION ====================
  function InvestmentProgression({ progression }) {
    const tradeNumbers = progression.trade_numbers || [];
    const investmentSizes = progression.investment_sizes || [];
  
    // Prepare data for chart
    const chartData = tradeNumbers.map((num, idx) => ({
      trade: `Trade ${idx + 1}`,
      tradeNumber: num || 0,
      investmentSize: investmentSizes[idx] || 0,
    }));
  
    const hasData = chartData.length > 0;
  
    return (
      <div
        className="p-8 rounded-3xl shadow-2xl mb-10 backdrop-blur-md"
        style={{
          background: 'linear-gradient(135deg, var(--color-secondary), var(--color-muted))',
          border: '1px solid var(--color-border)',
          color: 'var(--color-foreground)',
        }}
      >
        <div className="flex items-center mb-8">
          <span className="text-3xl mr-3">↗️</span>
          <h2 className="text-3xl font-extrabold">Investment Progression</h2>
        </div>
  
        {hasData ? (
          <>
            {/* Composed Chart: Bars for Size + Line for Trade Number */}
            <div className="rounded-2xl p-6 mb-8" style={{ backgroundColor: 'rgba(255,255,255,0.08)' }}>
              <h3 className="text-xl uppercase mb-6 text-center text-gray-200">
                Trade Progression Overview
              </h3>
              <ResponsiveContainer width="100%" height={400}>
                <ComposedChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                  <XAxis 
                    dataKey="trade" 
                    angle={-45} 
                    textAnchor="end" 
                    height={80} 
                    stroke="#ccc" 
                  />
                  <YAxis 
                    yAxisId="left" 
                    label={{ value: 'Investment Size ($)', angle: -90, position: 'insideLeft', style: { fill: '#ccc' } }} 
                    stroke="#ccc" 
                  />
                  <YAxis 
                    yAxisId="right" 
                    orientation="right" 
                    label={{ value: 'Trade Number', angle: 90, position: 'insideRight', style: { fill: '#FFD700' } }} 
                    stroke="#01FEC5" 
                  />
                   {/* <YAxis 
                label={{ value: 'Duration (days)', angle: -90, position: 'insideLeft', style: { fill: '#aaa' } }}
                tick={{ fill: '#ccc' }}
              /> */}
                  <Tooltip
                    contentStyle={{ backgroundColor: 'rgba(0,0,0,0.85)', border: '1px solid #01FEC5', borderRadius: '12px' }}
                    labelStyle={{ color: '#01FEC5' }}
                  />
                  <Legend verticalAlign="top" height={36} />
                  
                  {/* Investment Size Bars */}
                  <Bar 
                    yAxisId="left" 
                    dataKey="investmentSize" 
                    fill="#01FEC5" 
                    // name="Investment Size ($)" 
                    radius={[8, 8, 0, 0]} 
                  />
                  
                
                </ComposedChart>
              </ResponsiveContainer>
            </div>
  
            {/* Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 rounded-xl text-center" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}>
                <p className="text-sm text-gray-400">Total Trades</p>
                <p className="text-2xl font-bold" style={{ color: '#FFD700' }}>{tradeNumbers.length}</p>
              </div>
              <div className="p-4 rounded-xl text-center" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}>
                <p className="text-sm text-gray-400">Latest Trade #</p>
                <p className="text-2xl font-bold text-green-400">{tradeNumbers[tradeNumbers.length - 1] || 0}</p>
              </div>
              <div className="p-4 rounded-xl text-center" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}>
                <p className="text-sm text-gray-400">Total Invested</p>
                <p className="text-2xl font-bold" style={{ color: '#FFD700' }}>
                  ${investmentSizes.reduce((a, b) => a + (b || 0), 0).toLocaleString()}
                </p>
              </div>
              <div className="p-4 rounded-xl text-center" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}>
                <p className="text-sm text-gray-400">Avg Size</p>
                <p className="text-2xl font-bold" style={{ color: '#FFD700' }}>
                  ${(investmentSizes.reduce((a, b) => a + (b || 0), 0) / investmentSizes.length || 0).toFixed(0)}
                </p>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-16">
            <p className="text-xl text-gray-400">No progression data available yet</p>
          </div>
        )}
      </div>
    );
  }
 
  
  
  function TradeDurationAnalytics({ durations }) {
    if (!durations || !durations.completed_trades || durations.completed_trades.length === 0) {
      return (
        <div className="p-8 rounded-3xl shadow-2xl mb-10 backdrop-blur-md"
          style={{ backgroundColor: 'rgba(20,20,30,0.6)', border: '1px solid rgba(255,255,255,0.1)' }}>
          <div className="flex items-center mb-6">
            <span className="text-3xl mr-3">⏱️</span>
            <h2 className="text-3xl font-extrabold">Trade Duration Analytics</h2>
          </div>
          <div className="text-center py-20 text-gray-500 text-xl">
            No completed trades yet
          </div>
        </div>
      );
    }
  
    // Prepare data for line chart (sorted by trade number)
    const lineData = durations.completed_trades
      .sort((a, b) => (a.trade_number || 0) - (b.trade_number || 0))
      .map((trade) => ({
        name: `Trade ${trade.trade_number}`,
        action: trade.action || 'N/A',
        duration: trade.duration_days || 0,
      }));
  
    const average = durations.average_duration || 0;
  
    const CustomTooltip = ({ active, payload }) => {
      if (active && payload && payload.length) {
        const data = payload[0].payload;
        return (
          <div className="p-4 rounded-xl shadow-2xl border"
            style={{ backgroundColor: 'rgba(0,0,0,0.92)', borderColor: '#FFD700', backdropFilter: 'blur(10px)' }}>
            <p className="font-bold text-yellow-400">{data.name} ({data.action})</p>
            <p className="text-lg font-bold text-white mt-2">{data.duration} days</p>
            <p className="text-sm text-gray-300">
              {data.duration > average ? 'Above' : 'Below or equal'} average ({average.toFixed(1)} days)
            </p>
          </div>
        );
      }
      return null;
    };
  
    return (
      <div className="p-8 rounded-3xl shadow-2xl mb-10 backdrop-blur-md"
        style={{ backgroundColor: 'rgba(20,20,30,0.6)', border: '1px solid rgba(255,255,255,0.1)' }}>
  
        <div className="flex items-center mb-8">
          <span className="text-xl mr-3">⏱️</span>
          <h2 className="text-3xl font-extrabold">Trade Duration Analytics</h2>
        </div>
  
        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-10">
          {[
            { title: 'Average Duration', value: `${average.toFixed(1)} days`, icon: '⏱️' },
            { title: 'Longest Trade', value: `${durations.max_duration || 0} days`, icon: '🏆' },
            { title: 'Shortest Trade', value: `${durations.min_duration || 0} days`, icon: '⚡' },
          ].map((stat, i) => (
            <div key={i}
              className="p-6 rounded-2xl text-center backdrop-blur-sm transition-all hover:scale-105"
              style={{ backgroundColor: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,215,0,0.3)' }}>
              <div className="text-xl mb-4">{stat.icon}</div>
              <p className="text-sm uppercase tracking-wider text-gray-400">{stat.title}</p>
              <p className="text-3xl font-bold mt-3" style={{ color: '#01FEC5' }}>
                {stat.value}
              </p>
            </div>
          ))}
        </div>
  
        {/* Line Chart */}
        <div className="rounded-2xl p-8"
          style={{ border: '1px solid rgba(255,215,0,0.2)' }}>
          <h3 className="text-xl uppercase font-semibold mb-8 text-center text-gray-200">
            Trade Duration Over Time
          </h3>
  
          <ResponsiveContainer width="100%" height={450}>
            <LineChart data={lineData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
              <XAxis 
                dataKey="name" 
                angle={-45} 
                textAnchor="end" 
                height={80}
                tick={{ fill: '#ccc', fontSize: 12 }}
              />
              <YAxis 
                label={{ value: 'Duration (days)', angle: -90, position: 'insideLeft', style: { fill: '#aaa' } }}
                tick={{ fill: '#ccc' }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Line 
                type="monotone" 
                dataKey="duration" 
                stroke="#01FEC5" 
                strokeWidth={4} 
                dot={{ fill: '#FFD700', r: 6 }} 
                activeDot={{ r: 8, stroke: '#FFA500', strokeWidth: 2 }}
                animationDuration={1200}
              />
              {/* Reference line for average */}
              
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  }

function ChartLiquidity({ results }) {

    const processedTrades = useMemo(() => {
        if (!results?.detailed_trades) return [];
        let cycleNum = 1;
        let currentCycle = null;
        return results.detailed_trades.map((trade) => {
            const processed = { ...trade };
            if (trade.action === "BUY" || trade.action === "OPEN POSITION") {
                if (currentCycle === null) {
                    currentCycle = cycleNum;
                    cycleNum++;
                }
                processed.side = "LONG";
                processed.cycle = currentCycle;
            } else if (trade.action === "SELL (TP)" || trade.action === "STOP LOSS") {
                processed.side = "LONG";
                processed.cycle = currentCycle;
                currentCycle = null;
            } else {
                processed.side = "LONG";
                processed.cycle = currentCycle || cycleNum;
            }
            processed.fee = (trade.entry_fee || 0) + (trade.exit_fee || 0);
            return processed;
        });
    }, [results]);

    return (
        <div
            className="min-h-screen"
            style={{
                color: 'var(--color-foreground)'
            }}
        >
            <div className="container mx-auto px-4 py-8">
                {/* <BacktestParameters input={results.Input} /> */}
                <SummaryCards summary={results.backtest_summary || {}} />
                <PriceChart results={results} processedTrades={processedTrades} />
                <TradeHistoryTable processedTrades={processedTrades} />
                <CurrentPosition currentPos={results.current_position_info || {}} />
                <InvestmentProgression progression={results.investment_progression || {}} />
                <TradeDurationAnalytics durations={results.trade_duration_analytics || {}} />
            </div>
        </div>
    );
}

export default ChartLiquidity;